'use client';

import { ArrowLeft, ArrowRight, Mail, MailCheck } from 'lucide-react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { Suspense, useState } from 'react';
import { FormError, IconInput } from '@/components/auth/fields';
import { Button } from '@/components/ui/button';
import { errorMessage, post } from '@/lib/api';

function ForgotForm() {
  const params = useSearchParams();
  const [email, setEmail] = useState(params.get('email') ?? '');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  /** null = form; true = e-mail sent (if the account exists); false = the platform sends no e-mail yet. */
  const [sent, setSent] = useState<boolean | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const r = await post<{ sent: boolean }>('/auth/password/forgot', { email });
      setSent(r.sent);
    } catch (err) {
      setError(errorMessage(err));
    }
    setLoading(false);
  }

  if (sent !== null) {
    return (
      <div className="space-y-5">
        <span className="flex size-11 items-center justify-center rounded-xl bg-lime/10 text-lime">
          <MailCheck className="size-5" />
        </span>
        {sent ? (
          <div>
            <h2 className="text-2xl font-semibold tracking-tight">Confira seu e-mail</h2>
            <p className="mt-2 text-sm leading-relaxed text-fg-2">
              Se <span className="font-medium text-fg">{email}</span> tiver conta na Invexa, enviamos um link para criar uma nova senha. Ele vale por 1 hora.
            </p>
            <p className="mt-3 text-xs leading-relaxed text-fg-3">Não chegou? Olhe a caixa de spam ou peça de novo em alguns minutos.</p>
          </div>
        ) : (
          <div>
            <h2 className="text-2xl font-semibold tracking-tight">Fale com o suporte</h2>
            <p className="mt-2 text-sm leading-relaxed text-fg-2">
              O envio por e-mail ainda não está disponível. Chame o suporte ou quem te indicou a Invexa: eles enviam um link para você criar uma nova senha.
            </p>
          </div>
        )}
        <Button asChild variant="secondary" size="lg" className="h-12 w-full rounded-xl text-[15px]">
          <Link href="/login">
            <ArrowLeft /> Voltar para o login
          </Link>
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="mb-6">
        <h2 className="text-2xl font-semibold tracking-tight">Esqueceu a senha?</h2>
        <p className="mt-1 text-sm text-fg-2">Digite o e-mail da sua conta. Enviamos um link para você criar uma nova.</p>
      </div>
      <IconInput icon={Mail} label="E-mail" name="email" type="email" autoComplete="email" inputMode="email" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} placeholder="voce@email.com" />
      {error && <FormError>{error}</FormError>}
      <Button type="submit" variant="primary" size="lg" className="h-12 w-full rounded-xl text-[15px]" loading={loading}>
        Enviar link <ArrowRight />
      </Button>
      <p className="pt-2 text-center text-sm text-fg-3">
        Lembrou?{' '}
        <Link href="/login" className="font-medium text-lime hover:underline">
          Entrar
        </Link>
      </p>
    </form>
  );
}

export default function ForgotPasswordPage() {
  return (
    <Suspense>
      <ForgotForm />
    </Suspense>
  );
}
